import { useIsAuthenticated } from '@azure/msal-react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { http } from '@/shared/api/http';
import { delay } from '@/shared/api/mock';
import { env } from '@/shared/config/env';
import { toDisplayUser } from './displayUser';
import { accountEndpoints } from './endpoints';
import type { CurrentUser } from './types';

const mockUser: CurrentUser = {
  userId: 1,
  firstName: 'Alex',
  lastName: 'Morgan',
  emailAddress: 'alex.morgan@example.com',
  languageCode: 'en',
  mobileNumber: '',
  permissions: [],
};

const fetchCurrentUser = async ({ signal }: { signal: AbortSignal }): Promise<CurrentUser> => {
  if (env.useMocks) {
    await delay(200);
    return mockUser;
  }
  return http.get<CurrentUser>(accountEndpoints.currentUser, { signal });
};

export const currentUserQuery = () =>
  queryOptions({
    queryKey: ['account', 'current-user'],
    queryFn: fetchCurrentUser,
    // Fetched once per session; cleared with the rest of the cache when the session ends.
    staleTime: Infinity,
  });

/** The signed-in user's profile. Only fetched while there is an MSAL account. */
export const useCurrentUser = () => {
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    ...currentUserQuery(),
    enabled: isAuthenticated,
    select: toDisplayUser,
  });
};
